import { Controller } from "stimulus"
import { getAudioPlayerController } from "utils/audio_player"

export default class extends Controller {
  static targets = [
    "queueList",
    "nowPlaying", "nowPlayingArtwork", "nowPlayingTitle", "nowPlayingPlayButton", "nowPlayingPlayButtonTemplate",
    "nextFrom", "nextFromLabel", "nextFromArtwork", "nextFromTitle", "nextFromMenu", "nextFromAddToQueueTemplate"
  ]

  connect() {
    this.currentEpisodeId = null

    this.onQueueChanged = this.handleQueueChanged.bind(this)
    window.addEventListener("supercast-queue-changed", this.onQueueChanged)
    // Rows, heading and clear-button state all arrive server-rendered, so there's nothing to
    // fetch on connect.

    this.onTimeUpdate = this.handleTimeUpdate.bind(this)
    window.addEventListener("supercast-timeupdate", this.onTimeUpdate)

    this.onMetadataBroadcast = this.handleMetadataBroadcast.bind(this)
    window.addEventListener("supercast-episode-metadata", this.onMetadataBroadcast)

    // Deferred: #fixed-player is data-turbo-permanent, but Turbo still detaches/reattaches it
    // during the swap, so media--audio-player's own connect() can still be re-running in this
    // same navigation - reading it synchronously here is a race. Pushing to the next tick lets
    // that controller finish (re)connecting first.
    setTimeout(() => this.syncToCurrentPlayerState(), 0)
  }

  // The audio player may already have an episode loaded (playing or paused) from before this
  // page was visited. Sync once so Now Playing/Next From reflect reality immediately instead of
  // waiting for the next native playing/pause/timeupdate event.
  syncToCurrentPlayerState() {
    const audioPlayer = getAudioPlayerController(this.application)
    if (!audioPlayer) return

    this.applyPlayerState({
      itemId: audioPlayer.itemIdValue,
      itemType: audioPlayer.itemTypeValue,
      isPlaying: !audioPlayer.player.paused
    })
  }

  disconnect() {
    window.removeEventListener("supercast-queue-changed", this.onQueueChanged)
    window.removeEventListener("supercast-timeupdate", this.onTimeUpdate)
    window.removeEventListener("supercast-episode-metadata", this.onMetadataBroadcast)
  }

  // A change this device made arrives with its rows already re-rendered from the mutation
  // response, so there is nothing left to do. Only a change from ANOTHER device - which reaches
  // us as a content-free signal - needs the rows fetched.
  handleQueueChanged(event) {
    if (event?.detail?.rendered) return

    this.renderQueue()
  }

  // Rows are server-rendered (the row's play button bridges to native on its own, which is what
  // makes this page work inside the native webview), so a refresh is a turbo_stream fetch rather
  // than string templating.
  renderQueue() {
    return fetch("/subscriber_v2/queue_items", {
      headers: { Accept: "text/vnd.turbo-stream.html" }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to render queue: ${response.status}`)
        return response.text()
      })
      .then((html) => Turbo.renderStreamMessage(html))
      .catch((error) => console.error(error))
  }

  // SortableJS reports indices; the server takes neighbour ids, so translate against the DOM
  // order it just produced.
  reorder(event) {
    const { fromIndex, toIndex } = event.detail
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return

    const rows = Array.from(this.queueListTarget.querySelectorAll("[data-queue-item-id]"))
    const moved = rows[toIndex]
    if (!moved) return

    return window.queueClient.moveItem(
      Number(moved.dataset.queueItemId),
      rows[toIndex - 1] ? Number(rows[toIndex - 1].dataset.queueItemId) : null,
      rows[toIndex + 1] ? Number(rows[toIndex + 1].dataset.queueItemId) : null
    )
  }

  remove(event) {
    const id = this.queueItemIdFor(event.target)
    if (id == null) return

    return window.queueClient.removeFromQueue(id)
  }

  // The play button handles playback itself; this only handles the dequeue half, intercepted on
  // capture so the click still reaches the button.
  dequeueUpTo(event) {
    const row = event.target.closest("[data-queue-playable]")
    if (!row || row.dataset.queuePlayable !== "true") return

    const id = this.queueItemIdFor(event.target)
    if (id == null) return

    window.queueClient.removeFromQueueUpTo(id).catch((error) => console.error(error))
  }

  queueItemIdFor(element) {
    const row = element.closest("[data-queue-item-id]")
    return row ? Number(row.dataset.queueItemId) : null
  }

  clear() {
    return window.queueClient.clearQueue()
  }

  // "Now Playing" has no server endpoint of its own - it's driven entirely by the same
  // supercast-timeupdate events the fixed player and play buttons already listen to (plus the
  // initial sync in syncToCurrentPlayerState).
  handleTimeUpdate(event) {
    this.applyPlayerState(event.detail)
  }

  applyPlayerState({ itemId, itemType }) {
    if (itemType !== "Episode" || !itemId) {
      this.hideNowPlaying()
      return
    }

    if (this.currentEpisodeId == itemId) {
      return
    }

    this.currentEpisodeId = itemId
    this.loadNowPlaying(itemId)
  }

  async loadNowPlaying(episodeId) {
    const broadcast = await this.waitForBroadcast(episodeId, 400)
    if (episodeId != this.currentEpisodeId) return
    if (broadcast) {
      this.renderNowPlaying(broadcast.episode)
      this.renderNextFrom(broadcast.nextEpisode, broadcast.playlistName)
      return
    }

    const response = await fetch(`/subscriber_v2/episodes/${episodeId}/metadata${window.location.search}`, {
      headers: { Accept: "application/json" }
    })

    if (episodeId != this.currentEpisodeId) {
      return
    }

    if (!response.ok) {
      this.hideNowPlaying()
      return
    }

    const { episode, nextEpisode, playlistName } = await response.json()
    if (episodeId != this.currentEpisodeId) {
      return
    }
    this.renderNowPlaying(episode)
    this.renderNextFrom(nextEpisode, playlistName)
  }

  handleMetadataBroadcast(event) {
    const data = event.detail
    if (data?.episode?.itemId == this.currentEpisodeId) {
      this._resolveBroadcast?.(data)
    }
  }

  waitForBroadcast(episodeId, timeoutMs) {
    return new Promise((resolve) => {
      this._resolveBroadcast = (data) => { clearTimeout(timer); this._resolveBroadcast = null; resolve(data) }
      const timer = setTimeout(() => { this._resolveBroadcast = null; resolve(null) }, timeoutMs)
    })
  }

  renderNowPlaying(episode) {
    if (!episode) {
      this.hideNowPlaying()
      return
    }

    this.nowPlayingArtworkTarget.src = episode.artworkUrl ?? ""
    this.nowPlayingArtworkTarget.classList.toggle("hidden", !episode.artworkUrl)
    this.nowPlayingTitleTarget.textContent = episode.title ?? ""
    this.nowPlayingPlayButtonTarget.innerHTML = ""
    this.nowPlayingPlayButtonTarget.appendChild(this.buildNowPlayingPlayButton(episode))
    this.nowPlayingTarget.classList.remove("hidden")
  }

  hideNowPlaying() {
    if (this.currentEpisodeId == null) {
      return
    }

    this.currentEpisodeId = null
    this.nowPlayingTarget.classList.add("hidden")
    this.nowPlayingPlayButtonTarget.innerHTML = ""
    this.nextFromTarget.classList.add("hidden")
  }

  // Cloned from nowPlayingPlayButtonTemplate and filled in from the metadata broadcast/fetch,
  // mirroring buildAddToQueueButton below. Once inserted, play-button's own controller connects
  // and (on native) hands itself off to the bridge component.
  buildNowPlayingPlayButton(episode) {
    const buttonNode = this.nowPlayingPlayButtonTemplateTarget.content.cloneNode(true)
    const playButton = buttonNode.querySelector('[data-controller="play-button"]')
    if (!playButton) return buttonNode

    playButton.setAttribute("data-play-button-item-id-value", episode.itemId)
    playButton.setAttribute("data-play-button-item-type-value", episode.itemType ?? "Episode")
    playButton.setAttribute("data-play-button-audio-url-value", episode.audioUrl ?? "")
    playButton.setAttribute("data-play-button-hls-url-value", episode.hlsUrl ?? "")
    playButton.setAttribute("data-play-button-is-aws-hls-value", episode.isAwsHls ?? "false")
    playButton.setAttribute("data-play-button-hls-token-value", episode.hlsToken ?? "")
    playButton.setAttribute("data-play-button-feed-item-url-value", episode.feedItemUrl ?? "")
    playButton.setAttribute("data-play-button-name-value", episode.title ?? "")
    playButton.setAttribute("data-play-button-channel-name-value", episode.showName ?? "")
    playButton.setAttribute("data-play-button-summary-value", episode.summary ?? "")
    playButton.setAttribute("data-play-button-artwork-url-value", episode.artworkUrl ?? "")

    return buttonNode
  }

  renderNextFrom(nextEpisode, playlistName) {
    if (!nextEpisode) {
      this.nextFromTarget.classList.add("hidden")
      return
    }

    this.nextFromLabelTarget.textContent = `Next From · ${playlistName ?? nextEpisode.showName ?? ""}`
    this.nextFromArtworkTarget.src = nextEpisode.artworkUrl ?? ""
    this.nextFromArtworkTarget.classList.toggle("hidden", !nextEpisode.artworkUrl)
    this.nextFromTitleTarget.textContent = nextEpisode.title ?? ""
    this.nextFromMenuTarget.innerHTML = ""
    if (nextEpisode.audioUrl || document.querySelector("html").dataset.hotwireNative) {
      this.nextFromMenuTarget.appendChild(this.buildAddToQueueButton(nextEpisode))
    }

    this.nextFromTarget.classList.remove("hidden")
  }

  buildAddToQueueButton(nextEpisode) {
    const buttonNode = this.nextFromAddToQueueTemplateTarget.content.cloneNode(true)
    const addButton = buttonNode.querySelector('[data-controller="add-to-queue"]')

    // The component can still decline to render (no queue access), in which case there's no
    // button to fill in.
    if (!addButton) {
      return buttonNode
    }

    addButton.setAttribute("data-add-to-queue-item-id-value", nextEpisode.itemId)
    addButton.setAttribute("data-add-to-queue-item-type-value", nextEpisode.itemType)

    return buttonNode
  }
}
